import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header';
import '../../css/QnaManagement/QnaDetail.css';

const API_BASE = 'http://13.124.224.246:7778'; // 배포면 http://13.124.224.246:7778

// userId를 문자열로 안전 추출
const getUserIdString = (userId) => {
  if (!userId) return '';
  if (typeof userId === 'string') return userId;
  if (typeof userId === 'object') return userId._id || '';
  return '';
};

// 질문/답변 객체에서 표시용 작성자 라벨을 최대한 뽑아냄
const getBestAuthorLabel = (obj, fallbackMap = {}) => {
  const idStr = getUserIdString(obj.userId);
  return (
    obj.userId?.nickname ||
    obj.authorNickname ||   // 프론트가 주입했을 가능성
    obj.nickname ||
    obj.username ||
    obj.email ||
    fallbackMap[idStr] ||   // 하이드레이션 결과
    idStr ||                // 마지막 수단: id라도 표시
    '알 수 없음'
  );
};

// 단건 유저 조회 (백엔드 라우트: /api/users/userinfo/:id)
const fetchUserLabelById = async (id, token) => {
  if (!id) return '';
  try {
    const res = await axios.get(`${API_BASE}/api/users/userinfo/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const u = res?.data?.user;
    if (!u) return '';
    return u.nickname || u.name || u.username || u.email || '';
  } catch (e) {
    console.warn('[userinfo/:id] fail', id, e?.response?.status, e?.message);
    return '';
  }
};

const QnaDetail = () => {
  const [qna, setQna] = useState(null);
  const [qnaAuthor, setQnaAuthor] = useState('');       // ✅ 질문 작성자 라벨
  const [answers, setAnswers] = useState([]);
  const [answerAuthors, setAnswerAuthors] = useState({}); // ✅ 답변 작성자 라벨 캐시 {userId: label}

  const [newAnswer, setNewAnswer] = useState('');
  const [editingAnswerId, setEditingAnswerId] = useState(null);
  const [editedAnswer, setEditedAnswer] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state?.id;

  // 상세 로드
  useEffect(() => {
    if (!id) {
      console.error('ID 정보가 없습니다.');
      return;
    }

    const fetchQnaDetail = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await axios.get(
          `${API_BASE}/api/qnaQuestion/getinfoByid/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.success) {
          const q = res.data.question;
          setQna(q);

          // ✅ 질문 작성자 닉네임 하이드레이션
          const label = getBestAuthorLabel(q);
          if (label === '알 수 없음' || label === getUserIdString(q.userId)) {
            const uid = getUserIdString(q.userId);
            const hydrated = await fetchUserLabelById(uid, token);
            setQnaAuthor(hydrated || uid || '알 수 없음');
          } else {
            setQnaAuthor(label);
          }
        } else {
          console.log('QnA 상세 데이터 로드 실패');
        }
      } catch (error) {
        console.error('QnA 상세 정보를 가져오는데 실패했습니다.', error);
      }
    };

    const fetchAnswers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await axios.get(
          `${API_BASE}/api/qnaQuestion/getAnswers/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.success) {
          const list = Array.isArray(res.data.answers) ? res.data.answers : [];
          setAnswers(list);

          // ✅ 답변 작성자 닉네임 하이드레이션 (옵션: 필요없으면 생략 가능)
          const ids = Array.from(
            new Set(
              list
                .map(a => getUserIdString(a.userId))
                .filter(Boolean)
            )
          );
          if (ids.length) {
            const entries = await Promise.all(
              ids.map(async uid => [uid, await fetchUserLabelById(uid, localStorage.getItem('token'))])
            );
            setAnswerAuthors(prev => ({
              ...prev,
              ...Object.fromEntries(entries.map(([k, v]) => [k, v || ''])),
            }));
          }
        } else {
          console.log('답변 데이터 로드 실패');
        }
      } catch (error) {
        console.error('답변 데이터를 가져오는데 실패했습니다.', error);
      }
    };

    fetchQnaDetail();
    fetchAnswers();
  }, [id]);

  const handleAnswerSave = async () => {
    if (!newAnswer.trim()) {
      alert('답변을 입력해주세요.');
      return;
    }
    if (!qna?.category) {
      alert('카테고리 정보가 없어 답변을 저장할 수 없습니다.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.post(
        `${API_BASE}/api/qnaQuestion/addAnswer/${id}`,
        { body: newAnswer, category: qna.category },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        // ✅ 기존 답변 보존
        setAnswers(prev => [res.data.answer, ...prev]);
        setNewAnswer('');

        // ✅ 방금 작성한 답변 작성자 라벨 보강 (내 계정 기준)
        const myId = getUserIdString(res.data.answer?.userId);
        if (myId && !answerAuthors[myId]) {
          const label = await fetchUserLabelById(myId, token);
          setAnswerAuthors(prev => ({ ...prev, [myId]: label || '' }));
        }

        alert('답변이 등록되었습니다.');
      } else {
        alert('답변 등록에 실패했습니다.');
      }
    } catch (error) {
      console.error('답변 저장 중 오류:', error);
      alert('서버와의 연결에 문제가 발생했습니다.');
    }
  };

  const handleDelete = async (answerId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.delete(
        `${API_BASE}/api/qnaAnswer/${answerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setAnswers(prev => prev.filter(a => a._id !== answerId));
        alert('삭제되었습니다.');
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('삭제 중 오류:', error);
      alert('삭제 중 문제가 발생했습니다.');
    }
  };

  const handleEditStart = (answer) => {
    setEditingAnswerId(answer._id);
    setEditedAnswer(answer.body);
  };

  const handleEditSave = async (answerId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.put(
        `${API_BASE}/api/qnaAnswer/${answerId}`,
        { body: editedAnswer },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setAnswers(prev => prev.map(a => (a._id === answerId ? res.data.answer : a)));
        setEditingAnswerId(null);
        setEditedAnswer('');
        alert('수정되었습니다.');
      } else {
        alert('수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('수정 중 오류:', error);
      alert('수정 중 문제가 발생했습니다.');
    }
  };

  if (!qna) return <div>로딩 중...</div>;

  // 질문/답변 렌더에 사용할 안전한 라벨
  const questionAuthor = qnaAuthor || getBestAuthorLabel(qna);

  return (
    <div className="qna-detail-container">
      <Header />
      <div className="qna-detail-content-wrapper">
        <table className="qna-detail-table">
          <h3>QnA 상세 정보</h3>
          <tbody>
            <tr>
              <th>제목</th>
              <td>{qna.title}</td>
            </tr>
            <tr>
              <th>카테고리</th>
              <td>{qna.category || '없음'}</td>
            </tr>
            <tr>
              <th>작성자</th>
              {/* ✅ 하이드레이션된 닉네임 사용 */}
              <td>{questionAuthor || '알 수 없음'}</td>
            </tr>
            <tr>
              <th>작성 날짜</th>
              <td>{new Date(qna.createdAt).toLocaleDateString()}</td>
            </tr>
            <tr>
              <th>내용</th>
              <td><div className="qna-content">{qna.body}</div></td>
            </tr>
          </tbody>
        </table>

        {answers.length === 0 ? (
          <div className="qna-answer-form">
            <h2>답변 작성</h2>
            <textarea
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              className="qna-textarea"
              placeholder="답변을 입력하세요."
            />
            <button className="qna-save-button" onClick={handleAnswerSave}>
              답변 저장
            </button>
          </div>
        ) : (
          <div className="qna-answer-section">
            <h2>답변</h2>
            {answers.map((answer) => {
              const authorId = getUserIdString(answer.userId);
              const author = getBestAuthorLabel(answer, answerAuthors);
              return (
                <table key={answer._id} className="qna-detail-table" style={{ marginBottom: '24px' }}>
                  <tbody>
                    <tr>
                      <th>작성자</th>
                      <td>{author || authorId || '알 수 없음'}</td>
                    </tr>
                    <tr>
                      <th>작성 날짜</th>
                      <td>{new Date(answer.createdAt).toLocaleDateString()}</td>
                    </tr>
                    <tr>
                      <th>답변 내용</th>
                      <td>
                        {editingAnswerId === answer._id ? (
                          <textarea
                            value={editedAnswer}
                            onChange={(e) => setEditedAnswer(e.target.value)}
                            className="qna-textarea"
                          />
                        ) : (
                          <div className="qna-content">{answer.body}</div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2">
                        <div className="qna-button-group">
                          {editingAnswerId === answer._id ? (
                            <>
                              <button onClick={() => handleEditSave(answer._id)} className="qna-button edit">저장</button>
                              <button onClick={() => setEditingAnswerId(null)} className="qna-button delete">취소</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => handleEditStart(answer)} className="qna-button edit">수정</button>
                              <button onClick={() => handleDelete(answer._id)} className="qna-button delete">삭제</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QnaDetail;
